// VacTruckSidePanel.tsx
// Single-column layout showing OI submitted line items for reconciliation.
import './VacTruckSidePanel.css';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ChevronsRight as ChevronsRightIcon, Pencil as PencilIcon, Paperclip as PaperclipIcon, FileText as FileTextIcon, Image as ImageIcon, Download as DownloadIcon, Eye as EyeIcon } from 'lucide-react';
import { OILineItem } from '../../../../contexts/tableDefinitions';
import { useApiData } from '../../../../contexts/apiService';
import { carriersMatch } from '../../../../helper_functions';

// ── RB quality fields for disposal OI cards (stored as rb_* columns on vac_truck_tickets) ──
const RB_DISPOSAL_FIELDS: {
    key: 'rb_iron_content' | 'rb_bsw' | 'rb_quality_flag' | 'rb_bbls_accepted';
    rbFormKey: 'rb_iron_content' | 'rb_bsw' | 'rb_quality_flag' | 'rb_bbls_accepted';
    label: string; unit: string;
    type: 'number' | 'text' | 'select';
    options?: string[];
}[] = [
    { key: 'rb_iron_content',  rbFormKey: 'rb_iron_content',  label: 'Iron Content',  unit: 'ppm', type: 'number' },
    { key: 'rb_bsw',           rbFormKey: 'rb_bsw',           label: 'BS&W',          unit: '%',   type: 'number' },
    { key: 'rb_quality_flag',  rbFormKey: 'rb_quality_flag',  label: 'Quality',       unit: '',    type: 'select', options: ['Accepted', 'Partial', 'Rejected'] },
    { key: 'rb_bbls_accepted', rbFormKey: 'rb_bbls_accepted', label: 'Bbls Accepted', unit: 'bbl', type: 'number' },
];

const BLANK_RB_FORM = {
    rb_iron_content:  '',
    rb_bsw:           '',
    rb_quality_flag:  '',
    rb_bbls_accepted: '',
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const TICKET_TYPE_LABELS: Record<string, string> = {
    standard_vac: 'Standard Vac',
    freshwater:   'Freshwater',
    disposal:     'Disposal',
    washout:      'Washout',
};

const OI_STATUS_CLASS: Record<string, string> = {
    'Approved': 'vacSP-oiBadge--approved',
    'Paid':     'vacSP-oiBadge--paid',
    'Submitted':'vacSP-oiBadge--submitted',
    'Disputed': 'vacSP-oiBadge--disputed',
};

const toDay = (v: any): string | null => {
    if (!v) return null;
    try { return new Date(v).toISOString().slice(0, 10); }
    catch { return null; }
};

const fmtVol = (v: number | null, label = 'gal') =>
    v == null ? null : `${v.toLocaleString()} ${label}`;

interface VacTruckSidePanelProps {
    isOpen: boolean;
    onClose: () => void;
    rowData: any | null;
}

const FormRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="vacSP-formRow">
        <label className="vacSP-formLabel">{label}</label>
        {children}
    </div>
);

const LineRow: React.FC<{ label: string; value: string; mono?: boolean; isMetadata?: boolean }> = ({ label, value, mono, isMetadata }) => (
    <div className={`vacSP-lineRow ${isMetadata ? 'vacSP-lineRow--meta' : ''}`}>
        <span className="vacSP-lineLabel">{label}</span>
        <span className={`vacSP-lineValue ${mono ? 'vacSP-lineValue--mono' : ''}`}>{value}</span>
    </div>
);

const MaybeField: React.FC<{
    label: string;
    value: string;
    mismatch?: boolean;
    oiValue?: string | null;
    wide?: boolean;
}> = ({ label, value, mismatch, oiValue, wide }) => (
    <div className={`vacSP-masterField ${wide ? 'vacSP-masterField--wide' : ''}`}>
        <span className="vacSP-masterLabel">{label}</span>
        <div className="vacSP-masterValueGroup">
            <span className={`vacSP-masterValue ${mismatch ? 'vacSP-masterValue--mismatch' : ''}`}>{value || '—'}</span>
            {mismatch && oiValue && (
                <span className="vacSP-masterOiValue">OI: {oiValue}</span>
            )}
        </div>
    </div>
);

const VacTruckSidePanel: React.FC<VacTruckSidePanelProps> = ({ 
    isOpen,
    onClose,
    rowData
}) => {
    const { fetchVacAttachment, fetchVacAttachmentsZip } = useApiData();
    const [localOiItems, setLocalOiItems] = useState<OILineItem[]>([]);
    const [editingOiId, setEditingOiId]   = useState<number | null>(null);
    const [oiRbForm, setOiRbForm]         = useState({ ...BLANK_RB_FORM });
    const [attachmentLoading, setAttachmentLoading] = useState<string | null>(null);
    const [downloadLoading, setDownloadLoading]     = useState<string | null>(null);
    const [zipLoading, setZipLoading]     = useState(false);

    const [previewModal, setPreviewModal] = useState<{ objectUrl: string; fileName: string; contentType: string } | null>(null);
    const closePreviewModal = useCallback(() => {
        setPreviewModal(null);
    }, []);

    // Revoke the object URL whenever previewModal is replaced or the component unmounts.
    useEffect(() => {
        return () => {
            if (previewModal) URL.revokeObjectURL(previewModal.objectUrl);
        };
    }, [previewModal]);

    // ── Derive attachment map once per rowData change ──
    const attachmentMap = useMemo<Record<string, string>>(() => {
        if (!rowData?.oi_attachments) return {};
        try { return JSON.parse(rowData.oi_attachments); }
        catch { return {}; }
    }, [rowData?.oi_attachments]);
    const attachmentEntries = Object.entries(attachmentMap);  // [[fileName, href], ...]

    
    const handlePreview = useCallback(async (href: string, fileName: string) => {
        setAttachmentLoading(href);
        try {
            const result = await fetchVacAttachment(href);
            if (!result) throw new Error('fetchVacAttachment returned null');
            const objectUrl = URL.createObjectURL(result.blob);
            setPreviewModal({ objectUrl, fileName, contentType: result.contentType });
        } catch (err) {
            console.error('Attachment preview failed:', err);
        } finally {
            setAttachmentLoading(null);
        }
    }, [fetchVacAttachment]);

    // ── Download a single attachment ──
    const handleDownload = useCallback(async (href: string, fileName: string) => {
        setDownloadLoading(href);
        try {
            const result = await fetchVacAttachment(href);
            if (!result) throw new Error('fetchVacAttachment returned null');
            const objectUrl = URL.createObjectURL(result.blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = fileName;
            a.click();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
        } catch (err) {
            console.error('Attachment download failed:', err);
        } finally {
            setDownloadLoading(null);
        }
    }, [fetchVacAttachment]);

    // ── Download all attachments as zip ──
    const handleDownloadAll = useCallback(async () => {
        if (!rowData?.oi_receipt_item_id) return;
        setZipLoading(true);
        try {
            const blob = await fetchVacAttachmentsZip(rowData.oi_receipt_item_id);
            if (!blob) throw new Error('fetchVacAttachmentsZip returned null');
            // Timestamp generated locally — Content-Disposition isn't reliably
            // accessible via fetch() without CORS expose-headers.
            const ts = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14) + 'Z';
            const zipName = `OI_ticket_${rowData.ot_ticket ?? rowData.oi_receipt_item_id}_${ts}.zip`;
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = zipName;
            a.click();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
        } catch (err) {
            console.error('Attachment zip download failed:', err);
        } finally {
            setZipLoading(false);
        }
    }, [fetchVacAttachmentsZip, rowData]);

    useEffect(() => {
        setLocalOiItems(rowData?.oi_line_items ?? []);
        setEditingOiId(null);
        setPreviewModal(null);
    }, [rowData]);

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (previewModal) {
                closePreviewModal();
            } else {
                onClose();
            }
        };
        if (isOpen) document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose, previewModal, closePreviewModal]);

    const handleStartOiEdit = (item: OILineItem) => {
        setEditingOiId(item.oi_line_item_number);
        setOiRbForm({
            rb_iron_content:  item.rb_iron_content  != null ? String(item.rb_iron_content)  : '',
            rb_bsw:           item.rb_bsw           != null ? String(item.rb_bsw)           : '',
            rb_quality_flag:  item.rb_quality_flag  ?? '',
            rb_bbls_accepted: item.rb_bbls_accepted != null ? String(item.rb_bbls_accepted) : '',
        });
    };

    // Stub: updates local state only — no API call yet.
    // Wire up a backend endpoint before removing the disabled state from the Save button.
    const handleSaveOiRb = (id: number) => {
        setLocalOiItems((prev) => prev.map((i) =>
            i.oi_line_item_number !== id ? i : {
                ...i,
                rb_iron_content:  oiRbForm.rb_iron_content  !== '' ? parseFloat(oiRbForm.rb_iron_content)  : null,
                rb_bsw:           oiRbForm.rb_bsw           !== '' ? parseFloat(oiRbForm.rb_bsw)           : null,
                rb_quality_flag:  oiRbForm.rb_quality_flag  || null,
                rb_bbls_accepted: oiRbForm.rb_bbls_accepted !== '' ? parseFloat(oiRbForm.rb_bbls_accepted) : null,
            }
        ));
        setEditingOiId(null);
    };

    return (
        <>
            {rowData && (() => {
                const oiItems: OILineItem[] = localOiItems;
                const hasHeaderMismatch =
                    (rowData.oi_producer != null && rowData.producer_name !== rowData.oi_producer) ||
                    (rowData.oi_lease    != null && rowData.lease_name    !== rowData.oi_lease)    ||
                    (rowData.oi_carrier  != null && !carriersMatch(rowData.carrier_name, rowData.oi_carrier))  ||
                    (rowData.oi_service_date != null && toDay(rowData.loadDate) !== toDay(rowData.oi_service_date));
                return (<>

            {/* ── Header ───────────────────────────────────────── */}
            <div className="vacSP-header vacSP-header--padded">
                <div className="vacSP-headerMeta">
                    <span className="vacSP-headerRid">{rowData.rid}</span>
                    {rowData.truck_ticket && (
                        <span className="vacSP-headerTicket">{rowData.truck_ticket}</span>
                    )}
                </div>
            </div>

            {/* ── Master row summary ───────────────────────────── */}
            <div className="vacSP-masterGrid">
                <MaybeField label="Load Date"    value={rowData.loadDate}      mismatch={rowData.oi_service_date != null && toDay(rowData.loadDate) !== toDay(rowData.oi_service_date)} oiValue={rowData.oi_service_date} />
                <MaybeField label="Producer"     value={rowData.producer_name} mismatch={rowData.oi_producer != null && rowData.producer_name !== rowData.oi_producer} oiValue={rowData.oi_producer} />
                <MaybeField label="Lease"        value={rowData.lease_name}    mismatch={rowData.oi_lease != null && rowData.lease_name !== rowData.oi_lease} oiValue={rowData.oi_lease} />
                <MaybeField label="Carrier"      value={rowData.carrier_name}  mismatch={rowData.oi_carrier != null && !carriersMatch(rowData.carrier_name, rowData.oi_carrier)} oiValue={rowData.oi_carrier} />
                <MaybeField label="Status"       value={rowData.status} />
                <MaybeField label="Type of Work" value={rowData.type_of_work} />
                {rowData.rb_notes && <MaybeField label="Notes" value={rowData.rb_notes} wide />}
            </div>

            {/* ── Mismatch banners ─────────────────────────────── */}
            {hasHeaderMismatch && (
                <div className="vacSP-banner vacSP-banner--alert">
                    Header mismatch — one or more fields differ between our schedule and the OI receipt
                </div>
            )}
            {rowData.ot_ticket && oiItems.length === 0 && rowData.status === 'Completed' && (
                <div className="vacSP-banner vacSP-banner--warning">
                    No OI receipts found — carrier has not submitted to OpenInvoice yet
                </div>
            )}

            <div className="vacSP-divider" />

            {/* ── OI line items ────────────────────────────────── */}
            <div className="vacSP-col">
                <div className="vacSP-colHeader vacSP-colHeader--oi">
                    OI Submitted
                    <span className="vacSP-colCount">{oiItems.length}</span>
                </div>
                {oiItems.length > 0 && (() => {
                    const totalHours       = oiItems.reduce((s, i) => s + (i.oi_hours ?? 0), 0);
                    const totalDisposalVol = oiItems.filter(i => i.ticket_type === 'disposal').reduce((s, i) => s + (i.oi_volume ?? 0), 0);
                    const totalFreshwater  = oiItems.filter(i => i.ticket_type === 'freshwater').reduce((s, i) => s + (i.oi_volume ?? 0), 0);
                    return (
                        <div className="vacSP-summary">
                            {totalHours       > 0 && <span className="vacSP-summaryItem"><span className="vacSP-summaryLabel">Hours</span>{totalHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h</span>}
                            {totalDisposalVol > 0 && <span className="vacSP-summaryItem"><span className="vacSP-summaryLabel">Disposal</span>{totalDisposalVol.toLocaleString()} bbl</span>}
                            {totalFreshwater  > 0 && <span className="vacSP-summaryItem"><span className="vacSP-summaryLabel">Freshwater</span>{totalFreshwater.toLocaleString()} gal</span>}
                        </div>
                    );
                })()}
                {oiItems.length === 0 ? (
                    <div className="vacSP-emptyCol">No OI receipts</div>
                ) : (
                    oiItems.map((item) => {
                        const isDisposal  = item.ticket_type === 'disposal';
                        const isEditingRb = editingOiId === item.oi_line_item_number;
                        const hasRbData   = isDisposal && (
                            item.rb_iron_content != null || item.rb_bsw != null ||
                            item.rb_quality_flag != null || item.rb_bbls_accepted != null
                        );
                        return (
                            <div key={item.oi_line_item_number} className={`vacSP-lineCard ${isDisposal ? 'vacSP-lineCard--editable' : ''}`}>
                                <div className="vacSP-lineCardType">
                                    {TICKET_TYPE_LABELS[item.ticket_type] ?? item.ticket_type}
                                    <span className={`vacSP-oiBadge ${OI_STATUS_CLASS[item.oi_status ?? ''] ?? ''}`}>{item.oi_status}</span>
                                    {isDisposal && !isEditingRb && (
                                        <div className="vacSP-cardActions">
                                            <button className="vacSP-cardActionBtn" title="Edit RB field data" onClick={() => handleStartOiEdit(item)}>
                                                <PencilIcon size={11} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {item.oi_service_date         && <LineRow label="Service Date"   value={item.oi_service_date} />}
                                {item.oi_invoice_number       && <LineRow label="Invoice"        value={item.oi_invoice_number} mono />}
                                {item.oi_volume != null && item.ticket_type === 'disposal'     && <LineRow label="Disp. Vol."     value={fmtVol(item.oi_volume, 'bbl')!} />}
                                {item.oi_volume != null && item.ticket_type === 'freshwater'   && <LineRow label="Haul Vol."      value={fmtVol(item.oi_volume)!} />}
                                {item.oi_volume != null && item.ticket_type === 'standard_vac' && <LineRow label="Volume"         value={fmtVol(item.oi_volume)!} />}
                                {item.oi_hours  != null       && <LineRow label="Hours"          value={`${item.oi_hours}h`} />}
                                {item.oi_disposal_ticket      && <LineRow label="Disp. Ticket"   value={item.oi_disposal_ticket} />}
                                {item.oi_disposal_location    && <LineRow label="Disp. Location" value={item.oi_disposal_location} />}
                                {item.oi_work_description     && <LineRow label="Description"    value={item.oi_work_description} />}
                                {isDisposal && (isEditingRb ? (
                                    <div className="vacSP-rbEdit">
                                        <div className="vacSP-metaDivider"><span>RB Field Data</span></div>
                                        <FormRow label="Iron Content (ppm)">
                                            <input type="number" className="vacSP-input" placeholder="—" value={oiRbForm.rb_iron_content} onChange={(e) => setOiRbForm(p => ({ ...p, rb_iron_content: e.target.value }))} />
                                        </FormRow>
                                        <FormRow label="BS&W (%)">
                                            <input type="number" className="vacSP-input" placeholder="—" step="0.1" value={oiRbForm.rb_bsw} onChange={(e) => setOiRbForm(p => ({ ...p, rb_bsw: e.target.value }))} />
                                        </FormRow>
                                        <FormRow label="Quality">
                                            <select className="vacSP-input" value={oiRbForm.rb_quality_flag} onChange={(e) => setOiRbForm(p => ({ ...p, rb_quality_flag: e.target.value }))}>
                                                <option value="">—</option>
                                                <option value="Accepted">Accepted</option>
                                                <option value="Partial">Partial</option>
                                                <option value="Rejected">Rejected</option>
                                            </select>
                                        </FormRow>
                                        <FormRow label="Bbls Accepted (bbl)">
                                            <input type="number" className="vacSP-input" placeholder="—" value={oiRbForm.rb_bbls_accepted} onChange={(e) => setOiRbForm(p => ({ ...p, rb_bbls_accepted: e.target.value }))} />
                                        </FormRow>
                                        <div className="vacSP-addFormActions">
                                            <button className="vacSP-btnCancel" onClick={() => setEditingOiId(null)}>Cancel</button>
                                            <button className="vacSP-btnSave" disabled title="RB field saving coming soon" onClick={() => handleSaveOiRb(item.oi_line_item_number)}>Save</button>
                                        </div>
                                    </div>
                                ) : hasRbData && (
                                    <div className="vacSP-metaSection">
                                        {RB_DISPOSAL_FIELDS.map(({ key, label, unit }) => {
                                            const val = item[key];
                                            if (val == null) return null;
                                            return <LineRow key={String(key)} label={label} value={unit ? `${val} ${unit}` : String(val)} isMetadata />;
                                        })}
                                    </div>
                                ))}
                            </div>
                        );
                    })
                )}
            </div>

            <div className="vacSP-divider" />

            {/* ── Attachments ─────────────────────────────────── */}
            <div className="vacSP-col">
                <div className="vacSP-colHeader vacSP-colHeader--attachments">
                    <span className="vacSP-attachments-title">
                        <PaperclipIcon size={12} style={{ marginRight: 4, flexShrink: 0 }} />
                        Attachments
                    </span>
                    <span className="vacSP-colCount">{attachmentEntries.length}</span>
                    {attachmentEntries.length > 0 && rowData?.oi_receipt_item_id && (
                        <button
                            className="vacSP-downloadAllBtn"
                            onClick={handleDownloadAll}
                            disabled={zipLoading}
                            title="Download all as ZIP"
                        >
                            <DownloadIcon size={12} />
                            {zipLoading ? 'Zipping…' : 'Download All'}
                        </button>
                    )}
                </div>
                {attachmentEntries.length === 0 ? (
                    <div className="vacSP-emptyCol">No attachments</div>
                ) : (
                    attachmentEntries.map(([fileName, href]) => {
                        const isPdf  = fileName.toLowerCase().endsWith('.pdf');
                        const isImg  = /\.(png|jpe?g|gif|webp|bmp)$/i.test(fileName);
                        const isLoadingFile = attachmentLoading === href;
                        const isDownloadingFile = downloadLoading === href;
                        return (
                            <div key={href} className="vacSP-attachmentRow">
                                <span className="vacSP-attachmentIcon">
                                    {isPdf  ? <FileTextIcon size={13} /> :
                                     isImg  ? <ImageIcon    size={13} /> :
                                              <PaperclipIcon size={13} />}
                                </span>
                                <span className="vacSP-attachmentName" title={fileName}>{fileName}</span>
                                <button
                                    className="vacSP-attachmentPreviewBtn"
                                    onClick={() => handlePreview(href, fileName)}
                                    disabled={isLoadingFile}
                                    title="Preview"
                                >
                                    {isLoadingFile
                                        ? <span className="vacSP-spinner" />
                                        : <EyeIcon size={12} />}
                                </button>
                                <button
                                    className="vacSP-attachmentPreviewBtn"
                                    onClick={() => handleDownload(href, fileName)}
                                    disabled={isDownloadingFile}
                                    title="Download"
                                >
                                    {isDownloadingFile
                                        ? <span className="vacSP-spinner" />
                                        : <DownloadIcon size={12} />}
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
                </>);
            })()}


            {previewModal && (
                <div className="vacSP-previewOverlay" onClick={closePreviewModal}>
                    <div
                        className="vacSP-previewModal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="vacSP-previewTitle"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="vacSP-previewModalHeader">
                            <span id="vacSP-previewTitle" className="vacSP-previewModalTitle" title={previewModal.fileName}>
                                {previewModal.fileName}
                            </span>
                            <button className="vacSP-previewModalClose" onClick={closePreviewModal} aria-label="Close preview">✕</button>
                        </div>
                        <div className="vacSP-previewModalBody">
                            {previewModal.contentType === 'application/pdf' ? (
                                <iframe
                                    src={previewModal.objectUrl}
                                    title={previewModal.fileName}
                                    className="vacSP-previewFrame"
                                />
                            ) : previewModal.contentType.startsWith('image/') ? (
                                <img
                                    src={previewModal.objectUrl}
                                    alt={previewModal.fileName}
                                    className="vacSP-previewImage"
                                />
                            ) : (
                                <div className="vacSP-previewUnsupported">
                                    <p>Preview not available for this file type.</p>
                                    <a href={previewModal.objectUrl} download={previewModal.fileName} className="vacSP-previewDownloadLink">
                                        Download {previewModal.fileName}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VacTruckSidePanel;
